/**
 * How a Forge Cloud project describes itself in the sidebar before it connects.
 *
 * Two things this answers that the snapshot builder around it should not have
 * to: what a registered-but-never-provisioned project may claim, and why its
 * route stays available anyway.
 */
import { resolveRuntimeActionRoute } from "../../environments/runtimeActionRouting";
import type { SidebarProjectSourceMetadata } from "./SidebarProjectItem";

/**
 * Copy for a project that is registered but has never been provisioned. The
 * sidebar must not imply a machine exists, and must tell the user what will
 * actually happen (a workspace is created on first use), in Forge vocabulary.
 */
const REGISTERED_CLOUD_PROJECT_DESCRIPTION =
  "Ready to use. Your cloud project starts when you open the first thread.";

export function buildPreConnectCloudSource(isProvisioned: boolean): SidebarProjectSourceMetadata {
  const actionRoute = resolveRuntimeActionRoute({
    actionKind: "project-write",
    surface: "cloud-official",
    workSource: "cloud-vm",
    executionRuntime: "cloud",
  });
  return {
    actionAuthority: "runtime",
    surface: "cloud-official",
    hostKind: "user-cloud-vm",
    workSource: "cloud-vm",
    executionRuntime: "cloud",
    modelRuntime: "cloud",
    // cloud-only: gating the connect step lives on `preConnectCloud`, NOT here —
    // actionRouteAvailable stays true so the shared row keeps delete enabled.
    linkState: "cloud-only",
    workspaceIdentity: null,
    hideLocalPath: true,
    label: "Cloud project",
    description: isProvisioned
      ? "This project runs in your Forge Cloud VM."
      : REGISTERED_CLOUD_PROJECT_DESCRIPTION,
    actionRouteLabel: actionRoute.title,
    actionRouteDescription: actionRoute.description,
    actionRouteAvailable: actionRoute.available,
    requiresPermissionGate: actionRoute.requiresPermissionGate,
  };
}
